import {
  ALargeSmall,
  ArrowDownWideNarrow,
  ArrowUpDown,
  ArrowUpNarrowWide
} from '@tamagui/lucide-icons';
import { Form } from '@unistocks/components/form/form';
import { WatchValues } from '@unistocks/components/form/components/watch-values';
import { oneOf, requiredSchema } from '@unistocks/components/form/validators';
import { useTableColumns } from '@unistocks/features/portfolios/hooks/use-table-columns';
import { PortfolioBase } from '@unistocks/features/portfolios/models/portfolio-base';
import { t } from '@unistocks/localization';
import { SectionCreateDto, SectionUpdateDto, SortOrder } from '@unistocks/proxy/axios-client';
import { isDefined } from '@unistocks/shared/utils/util';
import { SelectOption } from '@unistocks/types';
import { FormPropsBase } from '@unistocks/types/form-props';
import { useMemo } from 'react';
import { z } from 'zod';

export type SectionFormData = (SectionCreateDto | SectionUpdateDto) & {
  sorting: {
    property?: string | null;
    sortOrder?: SortOrder | null;
  };
};

export type AddSectionFormProps = FormPropsBase<SectionFormData> & {
  portfolio: PortfolioBase;
  initialValues?: SectionFormData;
};

const schemaFactory = () =>
  z.object({
    name: requiredSchema(z.string(), t('::Name')),
    sorting: z
      .object({
        property: z.string().nullish(),
        sortOrder: oneOf([SortOrder.Ascending, SortOrder.Descending]).nullish()
      })
      .nullish()
  });

export const AddOrUpdateSectionForm: React.FC<AddSectionFormProps> = ({
  initialValues,
  portfolio,
  isSubmitting,
  onSubmit
}) => {
  const schema = schemaFactory();
  const isUpdate = !!initialValues?.name;
  const { selected: selectedColumns } = useTableColumns(portfolio);

  const sortPropertyOptions = useMemo(() => {
    const baseOptions: SelectOption<string | null>[] = [
      {
        key: 'name',
        value: 'Name',
        title: t('AbpAccount::DisplayName:Name'),
        icon: <ALargeSmall color={'$unistocks'} />
      },
      ...selectedColumns
        .filter(({ sortProp }) => !!sortProp)
        .map(({ prop, sortProp, displayName, Icon }) => ({
          key: prop,
          value: sortProp!,
          title: displayName,
          icon: Icon ? <Icon color={'$unistocks'} /> : undefined
        }))
    ];

    return initialValues?.sorting.property === null
      ? [
          ...baseOptions,
          {
            key: 'manual',
            value: null,
            title: t('::Manual'),
            icon: <ArrowUpDown color={'$unistocks'} />
          }
        ]
      : baseOptions;
  }, [selectedColumns, initialValues?.sorting]);

  return (
    <Form<SectionFormData>
      schema={schema}
      triggerText={initialValues ? t('::Save') : t('::Add')}
      initialValues={initialValues}
      isLoading={isSubmitting}
      onSubmit={onSubmit}
    >
      <WatchValues<SectionFormData> names={['sorting.property']}>
        {([sortProperty]) => (
          <>
            <Form.Input name={'name'} label={t('AbpAccount::DisplayName:Name')} isRequired />
            <Form.RadioGroup
              name={'sorting.property'}
              label={t('::Portfolio:Sort:Property')}
              isRequired={isUpdate}
              options={sortPropertyOptions}
            />
            {isDefined(sortProperty) ? (
              <Form.RadioGroup
                name={'sorting.sortOrder'}
                label={t('::Portfolio:Sort:Order')}
                size="lg"
                options={[
                  {
                    key: 'ascending',
                    value: SortOrder.Ascending,
                    title: t('::Ascending'),
                    icon: <ArrowUpNarrowWide color={'$unistocks'} />
                  },
                  {
                    key: 'descending',
                    value: SortOrder.Descending,
                    title: t('::Descending'),
                    icon: <ArrowDownWideNarrow color={'$unistocks'} />
                  }
                ]}
                isRequired={isUpdate}
              />
            ) : null}
          </>
        )}
      </WatchValues>
    </Form>
  );
};

AddOrUpdateSectionForm.displayName = 'AddOrUpdateSectionForm';
